import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { formatDate, formatDistanceToNow } from "date-fns";
import { MouseEvent } from "react";

import { useTheme } from "../../components/ui-v2";
import { useDrive } from "./DriveContext";
import { DriveGridItem } from "./DriveGridItem";
import DriveItem, { EllipsisButton } from "./DriveItem";
import {
  DriveReferenceSetFragment,
  DriveStyleFragment,
} from "./queries.graphql";
import { DriveViewType } from "./types";
import { tableViewColumns } from "./utils";

type DriveStyleLikeFragment = DriveStyleFragment | DriveReferenceSetFragment;

type DriveStyleViewProps = {
  viewType: DriveViewType;
  style: DriveStyleLikeFragment;
  isSelected: boolean;
  isSelectable: boolean;
  onClick: (event: MouseEvent) => void;
  onDoubleClick: (event: MouseEvent) => void;
  onContextMenu: (event: MouseEvent) => void;
};

export default function DriveStyleView({
  viewType,
  style,
  isSelected,
  isSelectable,
  onClick,
  onDoubleClick,
  onContextMenu,
}: DriveStyleViewProps) {
  const theme = useTheme();
  const { actionsDisabled } = useDrive();

  const {
    attributes,
    listeners,
    setNodeRef: draggableNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `style__${style.id}`,
    data: {
      supports: ["style"],
      item: style,
    },
  });
  const draggableStyle = transform
    ? {
        ...(isDragging && {
          pointerEvents: "none" as const, // this is necessary to prevent the link from being clicked while dragging
        }),
      }
    : undefined;

  const renderInner = () => {
    switch (viewType) {
      case "table":
        return (
          <>
            {tableViewColumns.map((column, index: number) => {
              return (
                <Box
                  key={column.id}
                  sx={{
                    width: column.width,
                    flexGrow: column.width ? 0 : 1,
                    flexShrink: column.width ? 0 : 1,
                    overflow: "hidden",
                    display: "-webkit-box",
                    "-webkit-box-orient": "vertical",
                    "-webkit-line-clamp": "1",
                    paddingRight: 4,
                    paddingLeft: 4,

                    ...(index > 1 && {
                      fontWeight: 300,
                      color: theme.palette.secondary[2],
                    }),
                  }}
                >
                  {column.styleValue?.(style) || ""}
                </Box>
              );
            })}
            {!actionsDisabled && (
              <EllipsisButton onClick={onContextMenu} isSelected={isSelected} />
            )}
          </>
        );
      case "grid":
        return (
          <DriveGridItem
            fileType="style"
            text={style.name || "Untitled"}
            subText={formatDistanceToNow(style.createdAt, {
              addSuffix: true,
            })}
            isSelected={isSelected}
            isDragging={isDragging}
            onContextMenu={onContextMenu}
            onFileClick={onClick}
            onFileDoubleClick={onDoubleClick}
          />
        );
    }
  };

  return (
    <DriveItem
      title={`${style.name || "Untitled"} (created at ${formatDate(style.createdAt, "PPpp")})`}
      type="Style"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      draggableStyle={draggableStyle}
      isSelected={isSelected}
      isDragging={isDragging}
      isSelectable={isSelectable}
      viewType={viewType}
      ref={draggableNodeRef}
      {...listeners}
      {...attributes}
    >
      {renderInner()}
    </DriveItem>
  );
}
